/**
 * HTML Generator - Converts ContentBuilder elements to HTML
 * Maintains backward compatibility with existing PDF generation
 */

/**
 * Generate HTML from ContentBuilder elements
 * @param {Array} elements - Element tree
 * @param {Object} options - Generation options
 * @returns {String} HTML string
 */
export const generateHTML = (elements, options = {}) => {
  const { mode = 'form', includeStyles = false } = options;

  let html = '';

  // Add wrapper styles if requested
  if (includeStyles) {
    html += `<style>
      /* Form Display Styles - Single Source of Truth */

      /* Layout Containers */
      .builder-section {
        width: 100%;
        position: relative;
        box-sizing: border-box;
        margin: 1rem 0;
      }

      .builder-row {
        display: grid;
        grid-template-columns: repeat(12, 1fr);
        width: 100%;
        gap: 1rem;
        box-sizing: border-box;
        margin: 0.5rem 0;
      }

      .builder-column {
        display: flex;
        flex-direction: column;
        box-sizing: border-box;
        min-width: 0;
      }

      /* Column spans */
      .builder-column[data-span="1"] { grid-column: span 1; }
      .builder-column[data-span="2"] { grid-column: span 2; }
      .builder-column[data-span="3"] { grid-column: span 3; }
      .builder-column[data-span="4"] { grid-column: span 4; }
      .builder-column[data-span="5"] { grid-column: span 5; }
      .builder-column[data-span="6"] { grid-column: span 6; }
      .builder-column[data-span="7"] { grid-column: span 7; }
      .builder-column[data-span="8"] { grid-column: span 8; }
      .builder-column[data-span="9"] { grid-column: span 9; }
      .builder-column[data-span="10"] { grid-column: span 10; }
      .builder-column[data-span="11"] { grid-column: span 11; }
      .builder-column[data-span="12"] { grid-column: span 12; }

      /* Form Fields */
      .form-field {
        margin: 1rem 0;
        page-break-inside: avoid;
      }

      .form-label {
        display: block;
        margin-bottom: 0.5rem;
        font-weight: 600;
        color: #333;
      }

      /* Form Inputs */
      .form-text, .form-email, .form-number, .form-tel,
      .form-textarea, .form-select {
        width: 100%;
        padding: 0.5rem;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-family: inherit;
        font-size: 1rem;
        box-sizing: border-box;
      }

      .form-textarea {
        resize: vertical;
        min-height: 80px;
      }

      .form-select {
        background-color: white;
        cursor: pointer;
      }

      /* Filled Values (for PDF) */
      .form-filled-value {
        padding: 0.5rem;
        border: 1px solid #ddd;
        border-radius: 4px;
        background-color: #f9f9f9;
        min-height: 1.5rem;
      }

      .form-filled-value.multiline {
        white-space: pre-wrap;
      }

      .form-filled-value.empty {
        color: #999;
        font-style: italic;
      }

      /* Checkboxes & Radios */
      .form-checkbox-label, .form-radio-label {
        display: flex !important;
        align-items: center !important;
        gap: 0.5rem !important;
        margin: 0.5rem 0 !important;
        cursor: pointer;
      }

      .form-checkbox, .form-radio {
        width: auto !important;
        margin: 0 !important;
        flex-shrink: 0;
        cursor: pointer;
      }

      .form-checkbox-text, .form-radio-text {
        margin: 0;
        line-height: 1.5;
      }

      .form-radio-group {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      /* Static checkbox/radio display (for PDF) */
      .form-checkbox-static, .form-radio-static {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin: 0.5rem 0;
        page-break-inside: avoid;
      }

      .form-checkbox-indicator, .form-radio-indicator {
        font-size: 1.2em;
        line-height: 1;
      }

      /* Visual Grid & Cells */
      .visual-grid, .grid-container {
        display: grid;
        width: 100%;
        gap: 1rem;
        box-sizing: border-box;
        margin: 1rem 0;
      }

      .grid-cell {
        display: flex;
        flex-direction: column;
        border: 1px solid #ddd;
        padding: 1rem;
        box-sizing: border-box;
        min-height: 80px;
        background-color: white;
      }

      /* Content Elements */
      .form-heading {
        margin: 0.5rem 0 0.25rem 0;
        color: #333;
        line-height: 1.3;
      }

      .form-heading.h1 { font-size: 2rem; font-weight: 700; }
      .form-heading.h2 { font-size: 1.5rem; font-weight: 700; }
      .form-heading.h3 { font-size: 1.25rem; font-weight: 600; }
      .form-heading.h4 { font-size: 1.125rem; font-weight: 600; }
      .form-heading.h5 { font-size: 1rem; font-weight: 600; }
      .form-heading.h6 { font-size: 0.875rem; font-weight: 600; }

      .form-paragraph {
        margin: 0.25rem 0;
        line-height: 1.6;
        color: #333;
      }

      .form-paragraph.small { font-size: 0.875rem; }
      .form-paragraph.normal { font-size: 1rem; }
      .form-paragraph.large { font-size: 1.125rem; }

      .form-richtext {
        margin: 0.5rem 0;
        line-height: 1.6;
      }

      .form-divider {
        margin: 2rem 0;
        border: 0;
        border-top: 1px solid #ccc;
      }

      /* Responsive */
      @media (max-width: 768px) {
        .builder-row {
          grid-template-columns: 1fr;
        }
        .builder-column[data-span] {
          grid-column: 1 / -1 !important;
        }
      }

      /* Print-friendly */
      @media print {
        .builder-row {
          grid-template-columns: 1fr;
        }
        .builder-column[data-span] {
          grid-column: 1 / -1 !important;
        }
        .form-heading {
          page-break-after: avoid;
          page-break-inside: avoid;
        }
        .form-field {
          page-break-inside: avoid;
        }
        .form-checkbox-static, .form-radio-static {
          page-break-inside: avoid;
        }
        .visual-grid, .grid-container {
          page-break-inside: avoid;
        }
        .grid-cell {
          page-break-inside: avoid;
        }
        .form-text, .form-textarea, .form-select, .form-filled-value {
          border-color: #999;
        }
        .grid-cell {
          border-color: #999;
        }
      }
    </style>\n`;
  }

  // Generate HTML for each element
  elements.forEach(element => {
    html += generateElementHTML(element, mode);
  });

  return html;
};

/**
 * Generate HTML for a single element (recursive)
 */
const generateElementHTML = (element, mode) => {
  const { type, content, children } = element;

  // Handle container elements (with children)
  if (children && children.length > 0) {
    return generateContainerHTML(element, mode);
  }

  // Handle content elements
  switch (type) {
    case 'heading':
      return generateHeadingHTML(content);

    case 'paragraph':
      return generateParagraphHTML(content);

    case 'richtext':
      return generateRichTextHTML(content);

    case 'divider':
      return generateDividerHTML(content);

    case 'bullet-list':
      return generateBulletListHTML(content);

    case 'ordered-list':
      return generateOrderedListHTML(content);

    case 'text-input':
      return generateTextInputHTML(content);

    case 'textarea':
      return generateTextAreaHTML(content);

    case 'select':
      return generateSelectHTML(content);

    case 'checkbox':
      return generateCheckboxHTML(content);

    case 'radio':
      return generateRadioHTML(content);

    case 'signature':
      return generateSignatureHTML(content);

    default:
      console.warn(`Unknown element type: ${type}`);
      return '';
  }
};

/**
 * Generate HTML for container elements
 */
const generateContainerHTML = (element, mode) => {
  const { type, props, children } = element;
  let html = '';

  switch (type) {
    case 'section':
      const sectionStyle = [];
      if (props.backgroundColor && props.backgroundColor !== 'transparent') {
        sectionStyle.push(`background-color: ${props.backgroundColor}`);
      }
      html += `<div class="builder-section" data-padding="${props.padding}" data-max-width="${props.maxWidth}" style="${sectionStyle.join('; ')}">\n`;
      children.forEach(child => {
        html += generateElementHTML(child, mode);
      });
      html += `</div>\n`;
      break;

    case 'row':
      html += `<div class="builder-row" data-columns="${props.columns}" data-gap="${props.gap}" data-alignment="${props.alignment}">\n`;
      children.forEach(child => {
        html += generateElementHTML(child, mode);
      });
      html += `</div>\n`;
      break;

    case 'column':
      const columnStyle = [];
      const gapMap = { none: '0', small: '0.5rem', medium: '1rem', large: '1.5rem' };

      // Apply flex layout properties
      if (props.flexDirection) columnStyle.push(`flex-direction: ${props.flexDirection}`);
      if (props.justifyContent) columnStyle.push(`justify-content: ${props.justifyContent}`);
      if (props.alignItems) columnStyle.push(`align-items: ${props.alignItems}`);
      if (props.gap) columnStyle.push(`gap: ${gapMap[props.gap] || gapMap.medium}`);
      if (props.backgroundColor && props.backgroundColor !== 'transparent') {
        columnStyle.push(`background-color: ${props.backgroundColor}`);
      }

      html += `<div class="builder-column" data-span="${props.span || 12}" data-width="${props.width}" data-padding="${props.padding}" style="${columnStyle.join('; ')}">\n`;
      children.forEach(child => {
        html += generateElementHTML(child, mode);
      });
      html += `</div>\n`;
      break;

    case 'grid':
      html += `<div class="builder-grid" data-gap="${props.gap}">\n`;
      children.forEach(child => {
        html += generateElementHTML(child, mode);
      });
      html += `</div>\n`;
      break;

    case 'grid-container':
      const rows = props.rows || 2;
      const columns = props.columns || 2;
      const gap = props.gap || '1rem';
      html += `<div class="visual-grid" style="display: grid; grid-template-columns: repeat(${columns}, 1fr); grid-template-rows: repeat(${rows}, auto); gap: ${gap};">\n`;
      children.forEach(child => {
        html += generateElementHTML(child, mode);
      });
      html += `</div>\n`;
      break;

    case 'grid-cell':
      const cellStyle = [];
      const cellContentStyle = [];
      const cellGapMap = { none: '0', small: '0.25rem', medium: '0.5rem', large: '1rem' };

      if (props.padding) {
        const paddingMap = { none: '0', small: '0.5rem', medium: '1rem', large: '1.5rem' };
        cellStyle.push(`padding: ${paddingMap[props.padding] || paddingMap.medium}`);
      }
      if (props.backgroundColor && props.backgroundColor !== 'transparent') {
        cellStyle.push(`background-color: ${props.backgroundColor}`);
      }

      // Apply flex layout properties to grid-cell-content
      if (props.flexDirection) cellContentStyle.push(`flex-direction: ${props.flexDirection}`);
      if (props.justifyContent) cellContentStyle.push(`justify-content: ${props.justifyContent}`);
      if (props.alignItems) cellContentStyle.push(`align-items: ${props.alignItems}`);
      if (props.gap) cellContentStyle.push(`gap: ${cellGapMap[props.gap] || cellGapMap.small}`);

      html += `<div class="grid-cell" data-padding="${props.padding}" style="${cellStyle.join('; ')}">\n`;
      html += `<div class="grid-cell-content" style="${cellContentStyle.join('; ')}">\n`;
      children.forEach(child => {
        html += generateElementHTML(child, mode);
      });
      html += `</div>\n`; // Close grid-cell-content
      html += `</div>\n`; // Close grid-cell
      break;

    default:
      // Unknown container, just render children
      children.forEach(child => {
        html += generateElementHTML(child, mode);
      });
  }

  return html;
};

/**
 * Content element HTML generators
 */
const generateHeadingHTML = (content) => {
  const { text = 'Heading', level = 'h2', alignment = 'left', color = 'default' } = content;
  const style = [];
  if (alignment) style.push(`text-align: ${alignment}`);
  if (color && color !== 'default') style.push(`color: ${color}`);

  return `<${level} class="form-heading" style="${style.join('; ')}">${text}</${level}>\n`;
};

const generateParagraphHTML = (content) => {
  const { text = '', alignment = 'left', fontSize = 'normal' } = content;
  const style = [];
  if (alignment) style.push(`text-align: ${alignment}`);
  if (fontSize === 'small') style.push('font-size: 0.875rem');
  if (fontSize === 'large') style.push('font-size: 1.125rem');

  return `<p class="form-paragraph" style="${style.join('; ')}">${text}</p>\n`;
};

const generateRichTextHTML = (content) => {
  const { html = '', alignment = 'left', fontSize = 'normal' } = content;
  const style = [];
  if (alignment) style.push(`text-align: ${alignment}`);
  if (fontSize === 'small') style.push('font-size: 0.875rem');
  if (fontSize === 'large') style.push('font-size: 1.125rem');

  return `<div class="form-richtext" style="${style.join('; ')}">${html}</div>\n`;
};

const generateDividerHTML = (content) => {
  const { style = 'solid', width = '100%', color = '#cccccc', spacing = 'medium' } = content;
  const marginMap = { small: '1rem 0', medium: '2rem 0', large: '3rem 0' };

  return `<hr class="form-divider" style="border: 0; border-top: 1px ${style} ${color}; width: ${width}; margin: ${marginMap[spacing] || marginMap.medium};" />\n`;
};

const generateBulletListHTML = (content) => {
  const { items = [], bulletStyle = 'disc', alignment = 'left', fontSize = 'normal' } = content;
  const style = [];
  if (alignment) style.push(`text-align: ${alignment}`);
  if (fontSize === 'small') style.push('font-size: 0.875rem');
  if (fontSize === 'large') style.push('font-size: 1.125rem');
  style.push(`list-style-type: ${bulletStyle}`);
  style.push('margin: 0.5em 0');
  style.push('line-height: 1.6');
  style.push('padding-left: 1.5rem');

  let html = `<ul class="form-bullet-list" style="${style.join('; ')}">\n`;
  items.forEach(item => {
    html += `  <li style="margin-bottom: 0.25rem;">${item}</li>\n`;
  });
  html += `</ul>\n`;

  return html;
};

const generateOrderedListHTML = (content) => {
  const { items = [], numberStyle = 'decimal', alignment = 'left', fontSize = 'normal', startNumber = 1 } = content;
  const style = [];
  if (alignment) style.push(`text-align: ${alignment}`);
  if (fontSize === 'small') style.push('font-size: 0.875rem');
  if (fontSize === 'large') style.push('font-size: 1.125rem');
  style.push(`list-style-type: ${numberStyle}`);
  style.push('margin: 0.5em 0');
  style.push('line-height: 1.6');
  style.push('padding-left: 1.5rem');

  let html = `<ol class="form-ordered-list" style="${style.join('; ')}" start="${startNumber}">\n`;
  items.forEach(item => {
    html += `  <li style="margin-bottom: 0.25rem;">${item}</li>\n`;
  });
  html += `</ol>\n`;

  return html;
};

/**
 * Form element HTML generators
 */
const generateTextInputHTML = (content) => {
  const { label = 'Text Field', name = '', placeholder = '', required = false } = content;
  const fieldName = name || label.toLowerCase().replace(/[^a-z0-9]+/g, '_');

  return `<div class="form-field">
    <label class="form-label">${label}${required ? ' *' : ''}</label>
    <input type="text" class="form-text" name="${fieldName}" placeholder="${placeholder}" ${required ? 'required' : ''} />
  </div>\n`;
};

const generateTextAreaHTML = (content) => {
  const { label = 'Text Area', name = '', placeholder = '', required = false, rows = 4 } = content;
  const fieldName = name || label.toLowerCase().replace(/[^a-z0-9]+/g, '_');

  return `<div class="form-field">
    <label class="form-label">${label}${required ? ' *' : ''}</label>
    <textarea class="form-textarea" name="${fieldName}" placeholder="${placeholder}" rows="${rows}" ${required ? 'required' : ''}></textarea>
  </div>\n`;
};

const generateSelectHTML = (content) => {
  const { label = 'Dropdown', name = '', options = [], required = false, placeholder = 'Choose an option...' } = content;
  const fieldName = name || label.toLowerCase().replace(/[^a-z0-9]+/g, '_');

  let html = `<div class="form-field">
    <label class="form-label">${label}${required ? ' *' : ''}</label>
    <select class="form-select" name="${fieldName}" ${required ? 'required' : ''}>
      <option value="">${placeholder}</option>\n`;

  options.forEach(opt => {
    html += `      <option value="${opt}">${opt}</option>\n`;
  });

  html += `    </select>
  </div>\n`;

  return html;
};

const generateCheckboxHTML = (content) => {
  const { label = 'Checkbox', name = '', text = 'I agree', required = false } = content;
  // Use the checkbox text to generate unique field names if no custom name provided
  const fieldName = name || text.toLowerCase().replace(/[^a-z0-9]+/g, '_');

  return `<div class="form-field">
    <label class="form-checkbox-label">
      <input type="checkbox" class="form-checkbox" name="${fieldName}" ${required ? 'required' : ''} />
      <span class="form-checkbox-text">${text}</span>
    </label>
  </div>\n`;
};

const generateRadioHTML = (content) => {
  const { label = 'Radio Group', name = '', options = [], required = false } = content;
  const fieldName = name || label.toLowerCase().replace(/[^a-z0-9]+/g, '_');

  let html = `<div class="form-field">
    <label class="form-label">${label}${required ? ' *' : ''}</label>
    <div class="form-radio-group">\n`;

  options.forEach((opt, idx) => {
    html += `      <label class="form-radio-label">
        <input type="radio" class="form-radio" name="${fieldName}" value="${opt}" ${required && idx === 0 ? 'required' : ''} />
        <span class="form-radio-text">${opt}</span>
      </label>\n`;
  });

  html += `    </div>
  </div>\n`;

  return html;
};

const generateSignatureHTML = (content) => {
  const { label = 'Digital Signature', name = 'signature', required = true, instructionText = '', legalNoticeText = '' } = content;

  return `<div class="form-field form-signature-field">
    <label class="form-label">${label}${required ? ' *' : ''}</label>
    <div class="form-signature-pad"
         data-signature-field="${name}"
         data-instruction-text="${instructionText.replace(/"/g, '&quot;')}"
         data-legal-notice-text="${legalNoticeText.replace(/"/g, '&quot;')}">
      <div class="signature-placeholder">
        <span class="signature-icon">✍️</span>
        <p>Click here to sign digitally</p>
        <small>Your signature will be securely captured and embedded in the document</small>
      </div>
    </div>
  </div>\n`;
};

/**
 * Extract form field metadata from elements (for validation)
 */
export const extractFormFieldMetadata = (elements) => {
  const fields = [];

  const traverse = (els) => {
    els.forEach(el => {
      // Check if it's a form element
      const formTypes = ['text-input', 'textarea', 'select', 'checkbox', 'radio', 'signature'];
      if (formTypes.includes(el.type) && el.content) {
        const name = el.content.name || el.content.label?.toLowerCase().replace(/[^a-z0-9]+/g, '_');
        if (name) {
          fields.push({
            name,
            label: el.content.label,
            type: el.type,
            required: el.content.required || false
          });
        }
      }

      // Recurse into children
      if (el.children) {
        traverse(el.children);
      }
    });
  };

  traverse(elements);
  return fields;
};
